/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"

import { useSiteMetadata } from "~utils/hooks/use-site-metadata"

interface Props {
    readonly title?: string | null
    readonly description?: string | null
    readonly image?: string | null
    readonly keywords?: string[]
    readonly isNoIndex?: boolean
}

export default function SEO({
    title,
    description,
    image,
    keywords,
    isNoIndex,
}: Props) {
    const { pathname } = useLocation()
    const { site } = useSiteMetadata()

    const {
        defaultTitle,
        defaultImage,
        defaultDescription,
        siteUrl,
        titleTemplate,
        twitterUsername,
    } = site?.siteMetadata || {}

    const seo = {
        template: title && titleTemplate ? titleTemplate : undefined,
        title: title || defaultTitle || "",
        image: image || defaultImage || "",
        description: description || defaultDescription || "",
        url: `${siteUrl}${pathname}`,
    }

    return (
        <Helmet htmlAttributes={{ lang: "en" }} titleTemplate={seo.template}>
            <title>{seo.title}</title>
            <meta httpEquiv="Expires" content="0" />
            <meta httpEquiv="Pragma" content="no-cache" />
            <meta
                httpEquiv="Cache-Control"
                content="no-cache, no-store, must-revalidate"
            />
            {/* Primary Meta Tags */}
            <meta name="description" content={seo.description} />
            <meta name="image" content={seo.image} />
            {isNoIndex && <meta name="robots" content="noindex" />}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={seo.url} />
            <meta property="og:title" content={seo.title} key="ogtitle" />
            <meta
                property="og:description"
                content={seo.description}
                key="ogdesc"
            />
            <meta property="og:image" content={seo.image} key="ogimage" />
            {/* Twitter Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            {twitterUsername && (
                <meta name="twitter:creator" content={twitterUsername} />
            )}
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={seo.image} />
            {/* Keywords */}
            {keywords && keywords.length > 0 && (
                <meta name="keywords" content={keywords.join(", ")} />
            )}
        </Helmet>
    )
}
